import clsx from 'clsx'
import React, { useMemo, useEffect } from 'react'
import { useTimer } from 'react-timer-hook'
import { match, P } from 'ts-pattern'

import Tag from '@/components-latest/primitives/Tag'
import { textVariants } from '@/components-latest/primitives/Text'
import { formatTime } from '@/utils/time.utils'

import Styles from './TimerTag.module.scss'

interface ITimerTagProps {
  onTimerEnd?: () => void
  timeLeft: number
  totalTime: number
}

const TimerTag: React.FC<ITimerTagProps> = ({
  onTimerEnd,
  totalTime,
  timeLeft
}) => {
  const {
    totalSeconds: totalSecondsLeft,
    seconds,
    minutes,
    hours,
    days,
    restart
  } = useTimer({
    expiryTimestamp: new Date(Date.now() + timeLeft),
    autoStart: new Date().getTime() < Date.now() + timeLeft,
    onExpire: () => onTimerEnd?.()
  })

  useEffect(() => {
    if (timeLeft > 0) {
      restart(new Date(Date.now() + timeLeft), true)
    }
  }, [timeLeft, restart])

  const formattedHours = days ? days * 24 + hours : hours
  const formattedTimer = [
    ...(formattedHours ? [formattedHours] : []),
    minutes,
    seconds
  ]
    .map(formatTime)
    .join(':')

  const intent = useMemo(() => {
    const timerLength = totalTime / 1000
    const currentProgress = totalSecondsLeft / timerLength

    return match(currentProgress)
      .with(
        P.when(n => n >= 0.6),
        () => 'positive' as const
      )
      .with(
        P.when(n => n < 0.6 && n >= 0.2),
        () => 'warning' as const
      )
      .with(
        P.when(n => n < 0.2 || n === Infinity),
        () => 'negative' as const
      )
      .otherwise(() => 'negative' as const)
  }, [totalTime, totalSecondsLeft])

  return (
    <Tag
      data-testid="timer-tag"
      className={clsx(
        Styles.timerTag,
        textVariants({ variant: 'nextBody', weight: 'semiBold' }),
        Styles[intent]
      )}
      variant="fill"
      size="medium"
      intent={intent}>
      {formattedTimer}
    </Tag>
  )
}

export default TimerTag
